import { Star } from "lucide-react";
import { Modal } from "@/components/ui/modal";
import { getInitials } from "@/utils/initials";

interface TestimonialModalProps {
  isOpen: boolean;
  onClose: () => void;
  testimonial: {
    quote: string;
    source: string;
    role?: string;
    year?: string;
    context?: string;
  };
  groupTitle?: string;
}

export function TestimonialModal({
  isOpen,
  onClose,
  testimonial,
  groupTitle
}: TestimonialModalProps) {
  const initials = getInitials(testimonial.source);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <div className="space-y-5">
        {/* Header: Avatar + Name */}
        <div className="flex items-start gap-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500 text-lg font-semibold text-white flex-shrink-0">
            {initials}
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-semibold text-emerald-100">
              {testimonial.source}
            </h3>
            {testimonial.role && (
              <span className="inline-flex items-center rounded-full bg-emerald-900/40 px-3 py-1 text-xs font-medium text-emerald-200 mt-2">
                {testimonial.role}
              </span>
            )}
          </div>
        </div>

        {/* Star Rating */}
        <div className="flex gap-1 text-emerald-400">
          {[...Array(5)].map((_, i) => (
            <Star key={i} className="h-5 w-5 fill-current" />
          ))}
        </div>

        {/* Quote */}
        <div className="rounded-xl bg-slate-800/40 border border-white/5 p-5">
          <p className="text-base leading-relaxed text-slate-200 italic">
            "{testimonial.quote}"
          </p>
        </div>

        {/* Footer Info */}
        <div className="flex flex-col gap-2 text-sm text-slate-400 border-t border-slate-700/50 pt-4">
          {testimonial.year && (
            <p>Shared in {testimonial.year}</p>
          )}
          {groupTitle && (
            <p className="text-emerald-300/70">
              Part of: {groupTitle}
            </p>
          )}
          {testimonial.context && (
            <p className="text-slate-500">
              Context: {testimonial.context}
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
}
